import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Navigation.css';

const getUgpAccessStatus = (ugp) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const checkRange = (startDate, endDate) => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start && today < start) return 'pending';
    if (end && today > end) return 'expired';
    return 'active';
  };

  // Role window: ugp start/end when override_period, else linked period dates
  if (ugp.override_period === true) {
    const roleStatus = checkRange(ugp.start_date, ugp.end_date);
    if (roleStatus !== 'active') return roleStatus;
  } else if (ugp.period_start_date) {
    // Null period_end_date = open-ended (Permanent)
    const roleStatus = checkRange(ugp.period_start_date, ugp.period_end_date);
    if (roleStatus !== 'active') return roleStatus;
  }

  if (ugp.parent_period_start_date) {
    const parentStatus = checkRange(
      ugp.parent_period_start_date,
      ugp.parent_period_end_date
    );
    if (parentStatus !== 'active') return parentStatus;
  }

  return 'active';
};

/**
 * True when this context's linked period has ended and access is only kept
 * open by an override (override_period + UGP dates). Working in such a
 * context is a stale-context write hazard: new activity is recorded under
 * the past period. See docs/anchor-and-extending-memberships.md.
 */
const isPastPeriodOverrideContext = (ugp) => {
  if (!ugp || ugp.override_period !== true) return false;
  if (!ugp.period_end_date) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today > new Date(ugp.period_end_date);
};

const getOrgInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
};

/** Org root from hierarchy superparent_id (not immediate team_nested parent). */
const resolveNavOrganizationLabel = (role) => {
  if (!role) return '';
  return (
    role.organization_group_name ||
    role.organization_groups_periods_title ||
    role.parent_group_name ||
    role.parent_groups_periods_title ||
    ''
  );
};

const resolveNavOrganizationLogo = (role) =>
  role?.organization_group_logo_url || role?.parent_group_logo_url || null;

const NavOrgLogo = ({ name, logoUrl }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  const showImage = logoUrl && !failed;

  return (
    <div className="nav-context-logo" aria-hidden>
      {showImage ? (
        <img
          src={logoUrl}
          alt=""
          className="nav-context-logo-img"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="nav-context-logo-fallback">{getOrgInitials(name)}</span>
      )}
    </div>
  );
};

const Navigation = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout, currentRole, loadingRole, loadCurrentRole, token, availableUgp, loadAvailableUgp } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const menuRef = useRef(null);
  const contextRef = useRef(null);

  // Note: availableUgp is now loaded in AuthContext and shared across components
  // No need to fetch here - just use the data from context

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (contextRef.current && !contextRef.current.contains(event.target)) {
        setIsContextOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const inPastPeriodContext = isPastPeriodOverrideContext(currentRole);

  const switchUgp = async (ugpId) => {
    if (!user?.id || !token) return;
    
    setSwitchingRole(true); // Show loading overlay
    
    try {
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_ugp_id: ugpId
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Reload current membership and org list after switching
        await loadCurrentRole();
        await loadAvailableUgp(); // Reload available roles (from AuthContext)
        setIsContextOpen(false);
        // Redirect to dashboard after role switch
        window.location.href = '/dashboard';
      } else {
        console.error('Failed to switch user_groups_periods:', data.message);
        setSwitchingRole(false); // Hide overlay on error
      }
    } catch (error) {
      console.error('Error switching user_groups_periods:', error);
      setSwitchingRole(false); // Hide overlay on error
    }
  };

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };

  const navigationContent = (
    <nav style={styles.nav}>
      <div style={styles.container}>
        <div style={styles.leftSection}>
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              style={{
                ...styles.logoContainer,
                ...(hoveredItem === 'home' ? styles.logoContainerHover : {})
              }}
              onMouseEnter={() => setHoveredItem('home')}
              onMouseLeave={() => setHoveredItem(null)}
              aria-label="Dashboard"
              aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
            >
              <img src="/acuity-sis-logo-clear.png" alt="Acuity Student Information System" style={styles.logoImage} />
            </Link>
          ) : (
            <>
              <Link
                to="/"
                style={{
                  ...styles.logoContainer,
                  ...(hoveredItem === 'logo' ? styles.logoContainerHover : {})
                }}
                onMouseEnter={() => setHoveredItem('logo')}
                onMouseLeave={() => setHoveredItem(null)}
                aria-label="User Information System — home"
              >
                <img src="/acuity-sis-logo-clear.png" alt="Acuity Student Information System" style={styles.logoImage} />
              </Link>
              <div style={styles.menuContainer} ref={menuRef}>
                <button
                  onClick={handleMenuClick}
                  onMouseEnter={() => setHoveredItem('menu')}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    ...styles.menuButton,
                    ...(hoveredItem === 'menu' ? styles.menuButtonHover : {})
                  }}
                  aria-label="Menu"
                  aria-expanded={isMenuOpen}
                >
                  <span style={{ fontSize: '18px' }}>☰</span>
                </button>
                {isMenuOpen && (
                  <div style={styles.dropdown}>
                    <Link
                      to="/auth"
                      onClick={handleMenuItemClick}
                      onMouseEnter={() => setHoveredItem('login')}
                      onMouseLeave={() => setHoveredItem(null)}
                      style={{
                        ...styles.menuItem,
                        ...styles.menuItemLogin,
                        ...(location.pathname === '/auth' ? styles.menuItemActive : {}),
                        ...(hoveredItem === 'login' ? styles.menuItemLoginHover : {})
                      }}
                    >
                      Login
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {isAuthenticated && (
          <div style={styles.rightSection}>
            {loadingRole ? (
              <span className="nav-context-loading">Loading…</span>
            ) : currentRole ? (
              <div className="nav-context-wrap" ref={contextRef}>
                <button
                  type="button"
                  className={`nav-context-trigger${isContextOpen ? ' nav-context-trigger--open' : ''}${inPastPeriodContext ? ' nav-context-trigger--pastperiod' : ''}`}
                  onClick={() => setIsContextOpen((open) => !open)}
                  aria-expanded={isContextOpen}
                  aria-haspopup="true"
                  title={inPastPeriodContext ? `You are working in a past period (${currentRole.period_title || 'ended period'}). New activity records under it.` : undefined}
                >
                  <div className="nav-context-trigger-text">
                    <span className="nav-context-org">
                      {resolveNavOrganizationLabel(currentRole) || 'No organization'}
                    </span>
                    <span className="nav-context-role">
                      {inPastPeriodContext && (
                        <span className="nav-context-pastperiod-dot" aria-hidden>⚠</span>
                      )}
                      {currentRole.groups_periods_title || currentRole.group_name || 'No team'}
                    </span>
                  </div>
                  <NavOrgLogo
                    name={resolveNavOrganizationLabel(currentRole)}
                    logoUrl={resolveNavOrganizationLogo(currentRole)}
                  />
                </button>

                {isContextOpen && (
                  <div className="nav-context-panel" role="dialog" aria-label="Context">
                    {inPastPeriodContext && (
                      <div className="nav-context-pastperiod-warning" role="alert">
                        <strong>Working in a past period.</strong> This context&rsquo;s period
                        ({currentRole.period_title || 'ended'}) is over — your access is kept
                        open by an override. Anything you create here is recorded under that
                        past period. Switch back to your current context when done.
                      </div>
                    )}
                    <div className="nav-context-details">
                      {currentRole.group_name && (
                        <div className="nav-context-row">
                          <span className="nav-context-label">Group</span>
                          <span className="nav-context-value">{currentRole.group_name}</span>
                        </div>
                      )}
                      {currentRole.period_title && (
                        <div className="nav-context-row">
                          <span className="nav-context-label">Period</span>
                          <span className="nav-context-value">{currentRole.period_title}</span>
                        </div>
                      )}
                      {resolveNavOrganizationLabel(currentRole) && (
                        <div className="nav-context-row">
                          <span className="nav-context-label">Organization</span>
                          <span className="nav-context-value">
                            {resolveNavOrganizationLabel(currentRole)}
                          </span>
                        </div>
                      )}
                    </div>

                    {availableUgp.length > 1 && (
                      <>
                        <hr className="nav-context-divider" />
                        <div className="nav-context-switch">
                          <span className="nav-context-switch-label">Switch context</span>
                          <div className="nav-context-options">
                            {availableUgp.map((ugp) => {
                              const isSelected = currentRole?.id === ugp.id;
                              const accessStatus = getUgpAccessStatus(ugp);
                              const isDisabled = isSelected || accessStatus !== 'active';
                              const isPastPeriod = isPastPeriodOverrideContext(ugp);

                              return (
                                <button
                                  key={ugp.id}
                                  type="button"
                                  className={`nav-context-option${isSelected ? ' nav-context-option--active' : ''}`}
                                  disabled={isDisabled}
                                  onClick={() => {
                                    if (isDisabled) return;
                                    if (isPastPeriod) {
                                      const periodLabel = ugp.period_title || 'a past period';
                                      const confirmed = window.confirm(
                                        `Heads up: this context's period (${periodLabel}) has already ended — your access is kept open by an override.\n\nAnything you create or change while switched into it will be recorded under that past period, not your current one.\n\nSwitch anyway?`
                                      );
                                      if (!confirmed) return;
                                    }
                                    switchUgp(ugp.id);
                                  }}
                                  title={
                                    accessStatus === 'pending'
                                      ? 'This membership has not started yet'
                                      : accessStatus === 'expired'
                                        ? 'This membership period has expired'
                                        : isPastPeriod
                                          ? 'This period has ended — access is kept open by an override. New activity records under the past period.'
                                          : undefined
                                  }
                                >
                                  <span className="nav-context-option-title">
                                    {ugp.groups_periods_title || ugp.group_name}
                                  </span>
                                  <span className="nav-context-option-meta">
                                    {resolveNavOrganizationLabel(ugp) ||
                                      ugp.parent_group_name ||
                                      ugp.parent_groups_periods_title}
                                  </span>
                                  {accessStatus === 'pending' && (
                                    <span className="nav-context-option-badge nav-context-option-badge--pending">
                                      Access Pending
                                    </span>
                                  )}
                                  {accessStatus === 'expired' && (
                                    <span className="nav-context-option-badge">Access expired</span>
                                  )}
                                  {accessStatus === 'active' && isPastPeriod && (
                                    <span className="nav-context-option-badge nav-context-option-badge--pastperiod">
                                      Past period — override access
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}

                    <hr className="nav-context-divider" />
                    <div className="nav-context-account">
                      <Link
                        to="/profile"
                        className={`nav-context-account-link${location.pathname === '/profile' ? ' nav-context-account-link--active' : ''}`}
                        onClick={() => setIsContextOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        type="button"
                        className="nav-context-account-logout"
                        onClick={() => {
                          setIsContextOpen(false);
                          logout();
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <span className="nav-context-loading">No active membership</span>
            )}
          </div>
        )}
      </div>
    </nav>
  );

  // Render switching overlay via portal to ensure it covers entire page
  return (
    <>
      {navigationContent}
      {switchingRole && createPortal(
        <div style={styles.switchingOverlay}>
          <div className="loading-spinner loading-spinner--sm" style={{ marginBottom: 16 }} />
          <div style={styles.switchingText}>Switching context...</div>
        </div>,
        document.body
      )}
    </>
  );
};

const styles = {
  switchingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000, // Higher than everything else
    pointerEvents: 'auto',
  },
  switchingText: {
    fontSize: '1rem',
    color: '#4b5563',
    fontWeight: 500,
  },
  nav: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    padding: '10px 0px',
  },
  container: {
    maxWidth: '1200px',
    marginLeft: 0,
    marginRight: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 20px',
    width: '100%',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    gap: '12px',
  },
  logoContainerHover: {
    opacity: 0.90,
  },
  logoImage: {
    display: 'block',
    height: '56px',
    width: 'auto',
    maxWidth: '420px',
    objectFit: 'contain',
  },
  menuContainer: {
    position: 'relative',
  },
  menuButton: {
    background: 'transparent',
    color: '#374151',
    border: 'none',
    padding: '8px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuButtonHover: {
    background: '#f3f4f6',
    color: '#1f2937',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    background: 'white',
    borderRadius: '10px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    minWidth: '160px',
    overflow: 'hidden',
    zIndex: 1001,
    border: '1px solid #e5e7eb',
  },
  menuItem: {
    display: 'block',
    color: '#374151',
    textDecoration: 'none',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '400',
    transition: 'all 0.15s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6',
  },
  menuItemActive: {
    background: '#f3f4f6',
    color: 'var(--color-brand)',
    fontWeight: '500',
  },
  menuItemHover: {
    background: '#f9fafb',
  },
  menuItemLogin: {
    background: '#f3f4f6',
    color: '#374151',
  },
  menuItemLoginHover: {
    background: '#e5e7eb',
  },
  menuItemLogout: {
    display: 'block',
    color: '#ef4444',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '400',
    transition: 'all 0.15s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    background: 'transparent',
  },
  menuItemLogoutHover: {
    background: '#fef2f2',
  },
  rightSection: {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },
};

export default Navigation;
